import {
  Component,
  DestroyRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomTranslateLoader } from '@core/i18n/custom-translate-loader';
import { AnswerModuleService } from '@core/services/answer-module.service';
import { DownloadLinkHandlerService } from '@core/services/download-link-handler.service';
import { RedirectService } from '@core/services/redirect.service';
import { ThemeService } from '@core/services/theme.service';
import { PAGE_TITLES, usePageTitle } from '@core/utils/page-title.util';
import { PagePreviewBaseComponent } from '@feature/preview-management/components/page-preview-base/page-preview-base.component';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { AnswerModuleResponse } from '@shared/models/answer-module-response';
import { PageProperties } from '@shared/models/page-properties';
import { QuestionAnswerRequest } from '@shared/models/question-answer-request';
import { QuestionAnswerResponseData } from '@shared/models/question-answer-response';
import { Question } from '@shared/models/views-models/question-view';
import { ClearErrors } from '@shared/state/error.state';
import { addResponseIdToAnswers } from '@shared/utils/form.util';
import { updateTextDirection } from '@shared/utils/helper.util';
import {
  cleanupViewportHeight,
  setupViewportHeight,
} from '@shared/utils/viewport.util';

import { CommonModule } from '@angular/common';
import { NavigationComponent } from '@core/components/navigation/navigation.component';
import { TopbarComponent } from '@core/components/topbar/topbar.component';
import { EditModeService } from '@core/services/edit-mode.service';
import { TranslateModule } from '@ngx-translate/core';
import { ConfirmationModalComponent } from '@shared/components/confirmation-modal/confirmation-modal.component';
import { PreviewMode } from '@shared/models/enums/preview-mode.enum';
import { catchError, EMPTY, exhaustMap, Observable, Subject } from 'rxjs';
import { SurveyFormComponent } from '../survey-form/survey-form.component';

@Component({
  selector: 'app-survey-page',
  templateUrl: './survey-page.component.html',
  styleUrl: './survey-page.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    TopbarComponent,
    SurveyFormComponent,
    NavigationComponent,
    ConfirmationModalComponent,
  ],
})
export class SurveyPageComponent
  extends PagePreviewBaseComponent
  implements OnInit, OnDestroy
{
  private setPageTitle = usePageTitle();
  validationErrorCount = 0;
  questions: Question[] = [];
  answers: QuestionAnswerResponseData[] = [];
  @ViewChild(SurveyFormComponent) surveyForm!: SurveyFormComponent;
  private readonly destroyRef = inject(DestroyRef);
  private beforeUnloadHandler = this.onBeforeUnload.bind(this);
  private readonly submitSubject$ = new Subject<QuestionAnswerRequest[]>();
  form?: FormGroup;
  isLoading = true;
  isNavigating = false;
  pageProperties: PageProperties = {
    enableProgressBar: false,
    isLastPage: false,
    percentProgress: 0,
  };
  private surveyTranslationKeys = [
    'validation.',
    'question.',
    'navigation.',
    'fileupload.',
    'editresponse.',
  ];
  showConfirmationModal = false;
  private pendingAnswers: QuestionAnswerRequest[] = [];

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    this.downloadLinkHandler.handleClick(event);
  }

  onBeforeUnload(event: Event): void {
    // Don't show warning for download links
    if (this.downloadLinkHandler.isDownload()) {
      this.downloadLinkHandler.reset();
      return;
    }
    event.preventDefault();
  }

  ngOnDestroy(): void {
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    this.downloadLinkHandler.reset();
    cleanupViewportHeight();
  }

  constructor(
    protected override redirectService: RedirectService,
    private answerService: AnswerModuleService,
    private route: ActivatedRoute,
    private router: Router,
    private translateLoader: CustomTranslateLoader,
    private translateService: TranslateService,
    protected override themeService: ThemeService,
    private store: Store,
    private downloadLinkHandler: DownloadLinkHandlerService,
    private editModeService: EditModeService,
  ) {
    super(redirectService, themeService);
  }

  canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
    return this.allowNavigation;
  }

  deactivateGuard(allowNavigation: boolean) {
    this.allowNavigation = allowNavigation;
  }

  onFormReady(form: FormGroup) {
    this.form = form;
  }

  updateValidationErrorCount() {
    const errorElements = document.querySelectorAll('.error-selector');
    this.validationErrorCount = errorElements.length;
  }
  updatePageProperties(pageProperties: PageProperties) {
    this.pageProperties = pageProperties;
  }

  private setPageProperties(response: AnswerModuleResponse) {
    this.pageProperties.isLastPage = response.isLastPage;
    this.pageProperties.enableProgressBar = response.enableProgressBar;
    this.pageProperties.percentProgress = response.percentProgress;
  }

  @HostListener('window:popstate', ['$event'])
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onPopState(event: PopStateEvent) {
    if (
      this.router?.lastSuccessfulNavigation?.extras?.state?.['data']
        ?.enableBackNavigation
    ) {
      this.answerService
        .getPreviousState(
          this.router.lastSuccessfulNavigation?.extras?.state?.['data']
            .responseId,
          this.router.lastSuccessfulNavigation?.extras?.state?.['data'].stateId,
        )
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          catchError(() => {
            this.allowNavigation = true;
            return [];
          }),
        )
        .subscribe((response) => {
          if (response.body) {
            this.handlePreviewMode(response.body);
          }
        });
    } else {
      history.pushState(null, '');
    }
  }

  ngOnInit(): void {
    this.setPageTitle(PAGE_TITLES.SURVEY.key, PAGE_TITLES.SURVEY.fallback);

    setupViewportHeight();
    this.setupSubmitPipeline();

    if (!this.previewMode) {
      window.addEventListener('beforeunload', this.beforeUnloadHandler);
    } else {
      this.surveyTranslationKeys.push('preview.');
    }
    this.route.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const response = history.state.data as AnswerModuleResponse;

        // Redirect to error page if no valid response data
        if (!response && !this.previewMode) {
          this.allowNavigation = true;
          this.router.navigate(['/error-page', '404']);
          return;
        }

        if (!response?.enableBackNavigation) {
          this.allowNavigation = false;
        }
        if (!this.previewMode && response) {
          this.isLoading = true;
          this.response = response;
          updateTextDirection(this.response.language);
        }
      });

    if (this.response) {
      this.translateLoader
        .getTranslation(this.response.language, this.surveyTranslationKeys, this.response.fallbackLanguage)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((translation) => {
          this.translateService.setTranslation(
            this.response!.language,
            translation,
            true,
          );
          this.translateService.use(this.response!.language);
        });
    }
  }

  protected override onResponseSet(value: AnswerModuleResponse): void {
    this.isNavigating = false;

    if (value.view.type === 'SURVEY_PAGE') {
      this.isLoading = true;
      this.questions = value.view.data.questions;
      this.answers = value.response?.data;
      this.setPageProperties(value);
    }
    this.validationErrorCount = 0;
    setTimeout(() => {
      this.isLoading = false;
      document.getElementById('survey-for-container')?.scrollTo(0, 0);
    }, 300);
  }

  submit(answers: QuestionAnswerRequest[]) {
    if (this.isNavigating) return;

    if (this.response?.stateId) {
      answers = addResponseIdToAnswers(this.response.responseId, answers);

      // Check if in edit mode and on last page - show confirmation modal
      if (
        this.editModeService.isEditMode &&
        this.pageProperties.isLastPage &&
        !this.previewMode
      ) {
        this.pendingAnswers = answers;
        this.showConfirmationModal = true;
        return;
      }

      // Normal submit flow
      this.submitToBackend(answers);
    }
  }

  onConfirmEditChanges(): void {
    if (this.isNavigating) return;

    this.showConfirmationModal = false;
    this.submitToBackend(this.pendingAnswers);
  }

  onCancelEditChanges(): void {
    this.showConfirmationModal = false;
    this.pendingAnswers = [];
  }

  private setupSubmitPipeline(): void {
    this.submitSubject$
      .pipe(
        exhaustMap((answers) => {
          this.isNavigating = true;
          this.isLoading = true;
          return this.answerService
            .getNextState(this.response!.responseId, this.response!.stateId!, answers)
            .pipe(
              catchError(() => {
                this.allowNavigation = true;
                this.isNavigating = false;
                this.isLoading = false;
                return EMPTY;
              }),
            );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        this.store.dispatch(new ClearErrors());

        window.addEventListener('beforeunload', function (event) {
          event.stopImmediatePropagation();
        });

        if (response.body && !this.previewMode) {
          this.allowNavigation = true;
          this.redirectService.redirectToView(response.body);
        } else if (response.body && this.previewMode) {
          this.isNavigating = false;
          this.responseChange.emit(response.body);
        } else {
          this.isNavigating = false;
          this.isLoading = false;
        }
      });
  }

  private submitToBackend(answers: QuestionAnswerRequest[]): void {
    if (!this.response?.stateId) return;
    this.submitSubject$.next(answers);
  }

  toPreviousState() {
    if (!this.response?.enableBackNavigation || this.isNavigating) return;

    this.isNavigating = true;
    this.isLoading = true;

    this.answerService
      .getPreviousState(this.response.responseId, this.response.stateId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => {
          this.allowNavigation = true;
          this.isNavigating = false;
          this.isLoading = false;
          return [];
        }),
      )
      .subscribe({
        next: (response) => {
          if (response.body && !this.previewMode) {
            this.allowNavigation = true;
            this.redirectService.redirectToView(response.body);
          } else if (response.body && this.previewMode) {
            this.isNavigating = false;
            this.responseChange.emit(response.body);
          } else {
            this.isNavigating = false;
            this.isLoading = false;
          }
        },
        error: (error) => {
          this.isNavigating = false;
          this.isLoading = false;
          console.error('Previous state error:', error);
        },
      });
  }

  protected readonly PreviewMode = PreviewMode;
}
